/**
 * What this device can actually do.
 *
 * The scanner has to run on a five-year-old phone as well as a laptop, and the
 * difference is not academic: four OCR passes over an upscaled image is a
 * couple of seconds on a desktop and can be fifteen on a budget handset with
 * two cores. Rather than ship one setting and hope, the pipeline is scaled to
 * what is in front of it.
 *
 * Nothing here gates the FEATURE — a weak device gets fewer passes and a
 * smaller working image, not a refusal. Only a genuine absence (no WebAssembly)
 * turns the scanner off, and then the reason is stated.
 */

export interface DeviceCapability {
  webAssembly: boolean;
  simd: boolean;
  workers: boolean;
  offscreenCanvas: boolean;
  camera: boolean;
  webGPU: boolean;
  /** navigator.hardwareConcurrency, or a conservative guess. */
  cores: number;
  /** navigator.deviceMemory in GB, when the browser reports it. */
  memoryGB: number | null;
  tier: 'high' | 'normal' | 'low';
  /** False only when OCR genuinely cannot run here. */
  canScan: boolean;
  reason: string | null;
}

export function detectCapability(): DeviceCapability {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;

  const webAssembly = typeof WebAssembly === 'object' && typeof WebAssembly.validate === 'function';
  const workers = typeof Worker === 'function';
  const offscreenCanvas = typeof OffscreenCanvas === 'function';
  const camera = Boolean(nav?.mediaDevices?.getUserMedia);
  const webGPU = typeof nav !== 'undefined' && 'gpu' in nav;
  const cores = nav?.hardwareConcurrency ?? 2;
  const memoryGB =
    typeof (nav as { deviceMemory?: number } | undefined)?.deviceMemory === 'number'
      ? (nav as unknown as { deviceMemory: number }).deviceMemory
      : null;

  const simd = webAssembly && simdSupported();

  let tier: DeviceCapability['tier'] = 'normal';
  if (cores >= 8 && (memoryGB === null || memoryGB >= 8) && simd) tier = 'high';
  else if (cores <= 3 || (memoryGB !== null && memoryGB <= 2)) tier = 'low';

  return {
    webAssembly,
    simd,
    workers,
    offscreenCanvas,
    camera,
    webGPU,
    cores,
    memoryGB,
    tier,
    canScan: webAssembly && workers,
    reason: !webAssembly
      ? 'This browser cannot run WebAssembly, which the on-device reader needs.'
      : !workers
        ? 'This browser cannot run background workers, which the scanner needs.'
        : null,
  };
}

/**
 * How many OCR passes to run.
 *
 * Consensus is the scanner's main defence against a confident wrong answer, so
 * this is the one place where a slow device genuinely gets a weaker result. Two
 * passes still cross-check; one cannot, and the confidence score says so.
 */
export function passesFor(capability: DeviceCapability): number {
  switch (capability.tier) {
    case 'high':
      return 4;
    case 'normal':
      return 3;
    case 'low':
      return 2;
  }
}

function simdSupported(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10,
        10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    );
  } catch {
    return false;
  }
}

/**
 * Whether the model is already cached, so the UI can distinguish
 * "still downloading" from "offline-ready".
 *
 * Reads the Cache Storage entry the service worker writes; a miss simply means
 * the first scan will download ~8 MB, which is worth warning about on a phone.
 */
export async function modelIsCached(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  try {
    const match = await caches.match('/tessdata/eng.traineddata');
    return match !== undefined;
  } catch {
    return false;
  }
}
